<template>
<div>
    <b-alert
        v-model="alertTimer"
        variant="danger"
        dismissible
        fade
    >
        {{ errorMessage }}
    </b-alert>
    <h1>{{ t['title'] }}</h1>
    <b-form-row class="form-group">
        <b-col>
            <b-form-select v-model="currentQueryIdx">
                <template v-slot:first>
                    <b-form-select-option :value="null">{{t['unsaved-query']}}</b-form-select-option>
                </template>
                <b-form-select-option v-for="([k, v]) in savedQueries" :key="k" :value="k">{{v.name}}</b-form-select-option>
            </b-form-select>
        </b-col>
        <b-col sm="3" v-if="currentQueryIdx === null"><b-button block v-b-modal.query-save-as :disabled="readonly">{{t['save-as']}}</b-button></b-col>
        <b-col sm="3" v-else><b-dropdown block right split @click="saveQuery" :text="t['save']" :disabled="readonly">
            <b-dropdown-item v-b-modal.query-save-as>{{t['save-as']}}</b-dropdown-item>
            <b-dropdown-item v-b-modal.query-to-widget>{{t['as-widget']}}</b-dropdown-item>
            <b-dropdown-item v-b-modal.query-delete>{{t['delete']}}</b-dropdown-item>
        </b-dropdown></b-col>
        <b-modal :title="t['save-as']" id="query-save-as" :ok-title="t['save']" :cancel-title="t['cancel']" @ok="saveExistingQuery">
            <label>
                {{t['name']}}
            </label>
            <b-input v-model="modal.name"/>
        </b-modal>
        <b-modal :title="t['as-widget']" id="query-to-widget" :ok-title="t['save']" :cancel-title="t['cancel']" @ok="saveQueryAsWidget">
            <b-form-group>
                <label>{{t.embeddable_widgets.fields.name}}</label>
                <b-input v-model="modalAsWidget.name"></b-input>
            </b-form-group>
            <b-form-group>
                <label>{{t.embeddable_widgets.fields.description}}</label>
                <b-textarea v-model="modalAsWidget.description"></b-textarea>
            </b-form-group>
            <b-form-group>
                <label>{{t.embeddable_widgets.fields.display}}</label>
                <b-input v-model="modalAsWidget.display"></b-input>
            </b-form-group>
        </b-modal>
        <b-modal :title="t['delete']" id="query-delete" :ok-title="t['delete']" :cancel-title="t['cancel']" @ok="deleteQuery" ok-variant="danger">
            {{t['delete-confirm']}}
        </b-modal>
    </b-form-row>
    <div style="width: 100%; height: 300px">
        <canvas id="chart-canvas"></canvas>
    </div>
    <b-button variant="primary" @click.prevent="updateChart" :style="{position: 'absolute', right: '1em'}">
        {{t['refresh']}} <fa-icon icon="sync" :class="{spin: loading}"/>
    </b-button>

    <b-tabs content-class="mt-3">
        <b-tab :title="t.sections.data">
            <form class="form">
                <time-range-selection @change="onTimeRangeSelectionChanged" :t="t['time-range']"
                                      :errors="errors"
                                      :from="currentQuery.from.definition()"
                                      :to="currentQuery.to.definition()">
                </time-range-selection>
                <b-form-row class="form-group">
                    <b-col md="8">
                        <b-form-row><b-col>
                            <label>{{t.data.values}}</label>
                            <b-form-row v-for="(item, idx) in currentQuery.values" :key="item.id">
                                <b-col>
                                    <b-form-group>
                                        <b-form-input type="text" v-model="item.name" placeholder="Name" aria-label="Name" :state="getValidationState(`values.${idx}.name`)"/>
                                        <b-form-invalid-feedback>{{getValidationError(`values.${idx}.name`)}}</b-form-invalid-feedback>
                                    </b-form-group>
                                </b-col>

                                <b-col>
                                    <b-form-group>
                                        <b-form-select class="form-control" v-model="item.field" :state="getValidationState(`values.${idx}.field`)">
                                            <template v-slot:first>
                                                <b-form-select-option :value="null" hidden>{{t.hints['select-field']}}</b-form-select-option>
                                            </template>
                                            <b-form-select-option value="speed">{{t.values.fields.speed}}</b-form-select-option>
                                            <b-form-select-option value="length">{{t.values.fields.length}}</b-form-select-option>
                                        </b-form-select>
                                        <b-form-invalid-feedback>{{getValidationError(`values.${idx}.field`)}}</b-form-invalid-feedback>
                                    </b-form-group>
                                </b-col>
                                <b-col>
                                    <b-form-group>
                                        <b-form-select class="form-control" v-model="item.aggregate" :state="getValidationState(`values.${idx}.aggregate`)">
                                            <template v-slot:first>
                                                <b-form-select-option :value="null" hidden>{{t.hints['select-aggregate']}}</b-form-select-option>
                                            </template>
                                            <b-form-select-option value="avg">{{t.values.aggregates.avg}}</b-form-select-option>
                                            <b-form-select-option value="max">{{t.values.aggregates.max}}</b-form-select-option>
                                            <b-form-select-option value="min">{{t.values.aggregates.min}}</b-form-select-option>
                                            <b-form-select-option value="count">{{t.values.aggregates.count}}</b-form-select-option>
                                            <b-form-select-option value="percentile">{{t.values.aggregates.percentile}}</b-form-select-option>
                                        </b-form-select>
                                        <b-form-invalid-feedback>{{getValidationError(`values.${idx}.aggregate`)}}</b-form-invalid-feedback>
                                    </b-form-group>
                                </b-col>
                                <b-col v-if="item.aggregate === 'percentile'">
                                    <b-form-group>
                                        <b-form-input type="number" class="form-control" v-model="item.parameter" aria-label="Aggregate" :state="getValidationState(`values.${idx}.parameter`)"/>
                                        <b-form-invalid-feedback>{{getValidationError(`values.${idx}.parameter`)}}</b-form-invalid-feedback>
                                    </b-form-group>
                                </b-col>
                                <b-col style="flex-grow: 0">
                                    <button class="btn btn-danger" @click.prevent="removeValueItem(idx)"><fa-icon icon="times"/></button>
                                </b-col>
                            </b-form-row>
                            <b-form-input :state="getValidationState('values')" style="display: none"/>
                            <b-form-invalid-feedback>{{getValidationError(`values`)}}</b-form-invalid-feedback>
                        </b-col></b-form-row>

                        <button class="btn btn-outline-primary" @click.prevent="addValueItem">{{t.add}}</button>
                    </b-col>
                    <b-col md="4">
                        <b-form-row class="form-group"><b-col>
                            <label id="interval-label">{{t.data.interval}}</label>
                            <div class="input-group">
<!--                                <input class="form-control" type="number" v-model="timeGroup.value" aria-labelledby="interval-label" min="1"/>-->
                                <b-form-select class="form-control" v-model="currentQuery.timeGroup.type" aria-labelledby="interval-label">
                                    <b-form-select-option value="days">{{t.values.interval.days}}</b-form-select-option>
                                    <b-form-select-option value="hours">{{t.values.interval.hours}}</b-form-select-option>
                                    <b-form-select-option value="weeks">{{t.values.interval.weeks}}</b-form-select-option>
                                </b-form-select>
                            </div>
                        </b-col></b-form-row>

                        <b-form-row><b-col>
                            <label>{{t.data['additional-groups']}}</label>
                            <b-form-row class="form-group" v-for="(item, idx) in currentQuery.groups" :key="item.id"><b-col>
                                <div :class="['input-group', {'is-invalid': getValidationState(`groups.${idx}.type`) === false}]">
                                    <b-form-select class="form-control" v-model="item.type" :state="getValidationState(`groups.${idx}.type`)">
                                        <option value="class">{{t.data.classes}}</option>
                                        <option value="device">{{t.data.devices}}</option>
                                        <option value="site">{{t.data.sites}}</option>
                                        <option value="direction">{{t.data.directions}}</option>
                                    </b-form-select>
                                    <div class="input-group-append">
                                        <button class="btn btn-danger" @click.prevent="removeGroupingItem(idx)"><fa-icon icon="times"/></button>
                                    </div>
                                </div>
                                <b-form-invalid-feedback>{{getValidationError(`groups.${idx}.type`)}}</b-form-invalid-feedback>
                            </b-col></b-form-row>
                        </b-col></b-form-row>
                        <button class="btn btn-outline-primary" @click.prevent="addGroupingItem">{{t.add}}</button>
                    </b-col>
                </b-form-row>
                <b-form-row class="form-group">
                    <b-col v-for="({title, category, withFilter}, idx) in [
                        {title: t.data.classes, category: 'classes', withFilter: false},
                        {title: t.data.directions, category: 'directions', withFilter: false},
                        {title: t.data.devices, category: 'devices', withFilter: true},
                        {title: t.data.sites, category: 'sites', withFilter: true}
                    ]" :key="category">
                        <checkbox-list
                            :title="title"
                            :t="t['checkbox-list']"
                            v-model="currentQuery.filter[idx].parameter"
                            :options="filterValues[category]"
                            :with-filter="withFilter"></checkbox-list>
                    </b-col>
                </b-form-row>
            </form>
        </b-tab>
        <b-tab :title="t.sections.display">
            <b-form-row class="form-group">
                <b-col sm="8">
                    <b-form-group>
                        <label>
                            {{t.display.types}}
                        </label>
                        <b-form-select class="form-control" v-model="currentQuery.display.type">
                                <option value="line">{{t.display.type.line}}</option>
                                <option value="bar">{{t.display.type.bar}}</option>
                        </b-form-select>
                    </b-form-group>
                    <b-form-group>
                        <label>
                            {{t.display.max}}
                        </label>
                        <b-form-input type="number" v-model.number="currentQuery.display.max" debounce="500"/>
                    </b-form-group>
                    <label class="font-weight-bold">
                        {{t.display['color-scheme'] || 'Farbschema'}}
                    </label>
                    <p v-if="!datasets.length">
                      {{t.display['color-scheme-no-values']}}
                    </p>
                    <b-form-group role="tab" v-for="ds in datasets" :key="ds.label">
                        <div>
                            <span>
                                <label>{{ds.label}}</label>
                                <span :id="`color-${ds.order}`" :style="`color: rgb(${ds.color.r}, ${ds.color.g}, ${ds.color.b})`">
                                    <fa-icon icon="minus"/>
                                </span>
                            </span>
                        </div>
                        <b-popover :target="`color-${ds.order}`" triggers="hover" placement="right" custom-class="fit-to-child">
                            <compact-picker :value="ds.color" @input="ds.color = $event.rgba"></compact-picker>
                        </b-popover>
                    </b-form-group>
                </b-col>
                <b-col sm="4">
                    <b-checkbox v-model="currentQuery.display.stacked">{{t.display.stacked}}</b-checkbox>
                    <template v-if="currentQuery.display.type === 'line'">
                        <b-checkbox v-model="currentQuery.display.drawLine">{{t.display['draw-line']}}</b-checkbox>
                        <template v-if="currentQuery.display.drawLine">
                            <b-checkbox v-model="currentQuery.display.filled">{{t.display.filled}}</b-checkbox>
                            {{t.display['line-width']}} <b-input type="number" v-model.number="currentQuery.display.lineWidth" min="0"/>
                        </template>
                        <b-checkbox v-model="currentQuery.display.drawPoints">{{t.display['draw-points']}}</b-checkbox>
                        <template v-if="currentQuery.display.drawPoints">
                            {{t.display['point-radius']}} <b-input type="number" v-model.number="currentQuery.display.pointRadius" min="0"/>
                        </template>
                    </template>
                </b-col>
            </b-form-row>
            <b-form-row>
                <b-col sm="12">

                </b-col>
            </b-form-row>
        </b-tab>
    </b-tabs>
</div>
</template>

<script>
    import {color, makeChart} from '../chart'
    import {v4 as uuid} from 'uuid'
    import {request} from '../api'
    import {getUnixTime} from 'date-fns'
    import TimeRangeSelection from './TimeRangeSelection'
    import CheckboxList from './CheckboxList'
    import colorscheme from '../chartjs-util/colorschemes'
    import dynamicDate from '../dynamicDate'
    import { Slider } from 'vue-color'
    import idx from "idx";
    import { omit } from 'lodash/fp'

    const fillAlpha = 0.5

    export default {
        name: "AnalyzeTrafficData.vue",
        components: {TimeRangeSelection, CheckboxList, 'compact-picker': Slider},
        props: ['t', 'initialQuery', 'readonly'],
        data() {
            return {
                alertTimer: 0,
                errorMessage: '',

                loading: false,

                queries: {
                    null: this.makeEmptyQuery()
                },
                currentQueryIdx: null,

                chart: null,

                filterValues: {
                    classes: [],
                    devices: [],
                    sites: [],
                    directions: [{value: 'incoming', label: this.t.values.direction.incoming}, {value: 'outgoing', label: this.t.values.direction.outgoing}]
                },

                datasets: [],

                modal: {
                    name: ''
                },
                modalAsWidget: {
                    name: '',
                    description: '',
                    display: ''
                },

                errors: {}
            }
        },
        beforeCreate() {
            ;(async () => {
                const result = await request('/classes', {
                    method: 'GET',
                })
                const {classes} = await result.json()
                this.$set(this.filterValues, 'classes', classes.map(x => ({
                    value: x.type, label: x.type, primary_color: x.primary_color
                })).concat([{value: 'unclassified', label: 'Unklassifiziert'}]))

                this.$set(this.queries[this.currentQueryIdx].filter[0], 'parameter', classes.map(x => x.type).concat(['unclassified']))
            })().catch(console.error)
            ;(async () => {
                const result = await request('/devices', {
                    method: 'GET',
                })
                const devices = await result.json()
                this.$set(this.filterValues, 'devices', devices.map(x => ({
                    value: x.id.toString(), label: x.serial
                })))
                this.$set(this.queries[this.currentQueryIdx].filter[2], 'parameter', devices.map(x => x.id.toString()))
            })().catch(console.error)
            ;(async () => {
                const result = await request('/sites', {
                    method: 'GET',
                })
                const sites = await result.json()
                this.$set(this.filterValues, 'sites', sites.map(x => ({
                    value: x.id.toString(), label: x.location
                })))
                this.$set(this.queries[this.currentQueryIdx].filter[3], 'parameter', sites.map(x => x.id.toString()))
            })().catch(console.error)
            ;(async () => {
                const result = await request('/traffic-data/queries', {
                    method: 'GET',
                })
                const queries = await result.json()

                this.queries = {
                    null: this.queries[null],
                    ...queries.reduce((acc, cur) => ({
                        ...acc,
                        [cur.id]: {
                            ...cur.definition,
                            name: cur.name,
                            from: dynamicDate(cur.definition.from, 'start'),
                            to: dynamicDate(cur.definition.to, 'end')
                        }
                    }), {})}
                if (this.initialQuery in this.queries)
                    this.currentQueryIdx = this.initialQuery
            })().catch(console.error)
        },
        mounted() {
            this.createChart()
        },
        watch: {
            'currentQuery.display': {
                deep: true,
                handler(nv) {
                    if (!this.chart) this.createChart()
                    else if (nv.type !== this.chart.config.type) this.createChart()
                    else this.refreshChart()
                }
            },
            datasets() {
                this.refreshChart()
            },
            currentQueryIdx() {
                this.updateChart()
            }
        },
        computed: {
            currentQuery() {
                return this.queries[this.currentQueryIdx]
            },
            savedQueries() {
                return Object.entries(this.queries).filter(([k]) => k !== 'null')
            }
        },
        methods: {
            onTimeRangeSelectionChanged(range) {
                this.currentQuery.from = range.from
                this.currentQuery.to = range.to
            },
            getValidationState(field) {
                return (field in this.errors) ? false : null
            },
            getValidationError(field) {
                return (this.errors[field] || []).join(' ')
            },
            addValueItem() {
                this.currentQuery.values = [...this.currentQuery.values, {
                    id: uuid(),
                    name: '',
                    field: null,
                    aggregate: null,
                    parameter: null
                }]
            },
            removeValueItem(idx) {
                this.$delete(this.currentQuery.values, idx)
            },
            addFilterItem() {
                this.currentQuery.filter = [...this.currentQuery.filter, {
                    id: uuid(),
                    type: null,
                    parameter: null
                }]
            },
            removeFilterItem(idx) {
                this.$delete(this.currentQuery.filter, idx)
            },
            addGroupingItem() {
                this.currentQuery.groups = [...this.currentQuery.groups, {
                    id: uuid(),
                    type: null
                }]
            },
            removeGroupingItem(idx) {
                this.$delete(this.currentQuery.groups, idx)
            },
            async updateChart() {
                this.loading = true
                this.clearErrors()

                try {
                    const currentQuery = this.currentQuery
                    const result = await request('/analyze/query?' + new URLSearchParams([
                        ['from', getUnixTime(currentQuery.from.value())],
                        ['to', getUnixTime(currentQuery.to.value())],
                        ['values', JSON.stringify(currentQuery.values.map(omit('id')))],
                        ['filter', JSON.stringify(currentQuery.filter.map(omit('id')))],
                        ['timeGroup', JSON.stringify(currentQuery.timeGroup)],
                        ['groups', JSON.stringify(currentQuery.groups)],
                    ]), {
                        method: 'GET',
                    })
                    if (!result.ok) {
                        this.datasets = []
                        this.errors = (await result.json()).errors
                        this.loading = false
                        return
                    }

                    const data = await result.json()
                    const colorGenerator = colorscheme()
                    const pickSemanticColor = (row, index) => {
                        const colorFromClass = (currentQuery.groups.length === 1 && currentQuery.groups[0].type === 'class')
                            ? idx(this.filterValues.classes, _ => _.find(x => x.value === row.groups[0]).primary_color)
                            : undefined
                        if (colorFromClass && !currentQuery.display.colors[index]) currentQuery.display.colors[index] = colorFromClass
                    }

                    this.datasets = data.map((v, idx) => {
                        const defaultColor = colorGenerator.next().value
                        pickSemanticColor(v, idx)

                        return ({
                            label: v.label,
                            order: idx,
                            get color() {
                                return color((currentQuery.display.colors || [])[idx] || defaultColor).rgb
                            },
                            set color(v) {
                                if (!currentQuery.display.colors) currentQuery.display.colors = []
                                Vue.set(currentQuery.display.colors, idx, v)
                            },
                            data: v.datapoints.map(([x, y]) => ({
                                x: new Date(x * 1000),
                                y: parseFloat(y)
                            }))
                        });
                    })
                } catch (e) {
                    console.error(e)
                }

                this.loading = false
            },
            refreshChart() {
                console.log('chart refresh')

                const currentQuery = this.currentQuery
                this.chart.data.datasets = this.datasets.map((x, idx) => {
                    return Object.assign({},
                        x,
                        currentQuery.display.stacked ? {stack: 'x'} : {},
                        {
                            backgroundColor: color(x.color).alpha(fillAlpha).rgbString(),
                            borderColor: color(x.color).rgbString(),
                            fill: currentQuery.display.filled && (idx === 0 ? 'origin' : '-1'),
                            lineTension: 0,
                            borderJoinStyle: 'round',
                            showLine: currentQuery.display.drawLine,
                            borderWidth: currentQuery.display.lineWidth,
                            radius: currentQuery.display.drawPoints ? currentQuery.display.pointRadius : 0,
                            hitRadius: currentQuery.display.drawPoints ? currentQuery.display.pointRadius : 3,
                            hoverRadius: currentQuery.display.drawPoints ? (currentQuery.display.pointRadius + 1) : 4
                        })
                })
                this.chart.options.scales.y = {
                    stacked: currentQuery.display.stacked,
                    beginAtZero: true,
                    max: currentQuery.display.max || undefined
                }

                this.chart.update('none')
            },
            createChart() {
                console.log('chart create')
                if (this.chart) {
                    this.chart.destroy()
                }

                const currentQuery = this.currentQuery

                const ctx = document.getElementById('chart-canvas')
                this.chart = makeChart(ctx, {
                    type: currentQuery.display.type,
                    xAxis: {
                      labelType: 'time',
                      stepUnit: 'day',
                      offset: currentQuery.display.type !== 'line'
                    },
                    yAxis: {
                      stacked: currentQuery.display.stacked,
                      max: currentQuery.display.max || undefined
                    },
                    data: {
                        datasets: this.datasets.map((x, idx) => {
                            return Object.assign({},
                                x,
                                currentQuery.display.stacked ? {stack: 'x'} : {},
                                {
                                    backgroundColor: color(x.color).alpha(fillAlpha).rgbString(),
                                    borderColor: color(x.color).rgbString(),
                                    fill: currentQuery.display.filled && (idx === 0 ? 'origin' : '-1'),
                                    lineTension: 0,
                                    borderJoinStyle: 'round',
                                    showLine: currentQuery.display.drawLine,
                                    borderWidth: currentQuery.display.lineWidth,
                                    radius: currentQuery.display.drawPoints ? currentQuery.display.pointRadius : 0,
                                    hitRadius: currentQuery.display.drawPoints ? currentQuery.display.pointRadius : 3,
                                    hoverRadius: currentQuery.display.drawPoints ? (currentQuery.display.pointRadius + 1) : 4
                                })
                        })
                    }
                })
            },
            cloneQuery(idx, name) {
                const existingQuery = this.queries[idx]
                return {
                    name,
                    from: existingQuery.from,
                    to: existingQuery.to,
                    values: existingQuery.values.map(v => ({
                        ...v, id: uuid()
                    })),
                    filter: existingQuery.filter.map(f => ({
                        ...f, id: uuid()
                    })),
                    groups: existingQuery.groups.map(g => ({
                        ...g, id: uuid()
                    })),
                    timeGroup: existingQuery.timeGroup,
                    display: existingQuery.display
                }
            },
            async saveExistingQuery(e) {
                const newQuery = this.cloneQuery(this.currentQueryIdx, this.modal.name)
                await this.saveNewQuery(e, newQuery)
            },
            async saveNewQuery(e, query = undefined) {
                query = query || this.currentQuery
                const result = await request('/traffic-data/queries', {
                    method: 'POST',
                    body: JSON.stringify({
                        name: this.modal.name,
                        definition: {
                            ...query,
                            from: query.from.definition(),
                            to: query.to.definition()
                        }
                    })
                })
                if (!result.ok) e.preventDefault()
                else {
                    const data = await result.json()
                    this.$set(this.queries, data.id, {...query, name: this.modal.name});
                    this.$set(this.queries, null, this.makeEmptyQuery());
                    this.modal.name = ''
                    await this.$nextTick()
                    this.currentQueryIdx = data.id
                }
            },
            async saveQuery(e) {
                const currentQuery = this.currentQuery
                const result = await request(`/traffic-data/queries/${this.currentQueryIdx}`, {
                    method: 'PUT',
                    body: JSON.stringify({
                        name: currentQuery.name,
                        definition: {
                            ...currentQuery,
                            from: currentQuery.from.definition(),
                            to: currentQuery.to.definition()
                        }
                    })
                })
            },
            async saveQueryAsWidget(e) {
                const result = await request('/embeddable_widgets', {
                    method: 'POST',
                    body: JSON.stringify({
                        type: 'query',
                        ...this.modalAsWidget,
                        parameters: {
                          query: this.currentQueryIdx
                        }
                    })
                })
                if (!result.ok) e.preventDefault()
                else
                {
                    const data = await result.json()
                    this.modalAsWidget.name = ''
                    this.modalAsWidget.description = ''
                    this.modalAsWidget.display = ''
                    window.location.href = `/embeddable_widgets/${data.id}`
                }
            },
            async deleteQuery(e) {
                const currentQuery = this.currentQuery
                const result = await request(`/traffic-data/queries/${this.currentQueryIdx}`, {
                    method: 'DELETE',
                })

                if (!result.ok) {
                    const body = await result.json()
                    this.setError(this.t.errors[body.error_code])
                    return
                }

                this.$delete(this.queries, this.currentQueryIdx)
                this.currentQueryIdx = null
            },
            makeEmptyQuery() {
                return {
                    name: 'Unsaved Query',
                    values: [{
                        id: uuid(),
                        name: '',
                        field: null,
                        aggregate: null,
                        parameter: null
                    }],
                    filter: [
                        {
                            id: uuid(),
                            type: 'classes',
                            parameter: []
                        },
                        {
                            id: uuid(),
                            type: 'direction',
                            parameter: ['incoming', 'outgoing']
                        },
                        {
                            id: uuid(),
                            type: 'devices',
                            parameter: []
                        },
                        {
                            id: uuid(),
                            type: 'sites',
                            parameter: []
                        }
                    ],
                    timeGroup: {
                        type: 'days',
                        value: 1
                    },
                    groups: [],
                    from: dynamicDate('', 'start'),
                    to: dynamicDate('', 'end'),
                    display: {
                        type: 'line',
                        max: null,
                        stacked: true,
                        filled: true,
                        drawLine: true,
                        lineWidth: 1,
                        drawPoints: false,
                        pointRadius: 3,
                        colors: []
                    },
                }
            },
            clearErrors() {
                this.errors = {}
            },
            setError(msg) {
                this.errorMessage = msg
                this.alertTimer = 5
            }
        }
    }
</script>

<style>
.spin {
    animation: spin 4s linear infinite;
}

@keyframes spin {
    100% {
        transform: rotate(360deg);
    }
}
</style>
